
import { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

export default function CtaSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-32 bg-[#0a0a0a] relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(0,212,255,0.08) 0%, transparent 70%)',
            animation: 'pulse-slow 4s ease-in-out infinite',
          }}
        />
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(0,255,136,0.06) 0%, transparent 70%)',
            animation: 'pulse-slow 4s ease-in-out infinite 2s',
          }}
        />
      </div>

      {/* Grid lines */}
      <div
        className="absolute inset-0 opacity-5 pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,212,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(0,212,255,0.5) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      />

      <div
        className="relative z-10 max-w-[900px] mx-auto px-6 text-center"
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(40px)',
          transition: 'opacity 0.8s ease, transform 0.8s ease',
        }}
      >
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#00d4ff]/30 bg-[#00d4ff]/5 backdrop-blur-sm mb-8">
          <span className="w-2 h-2 rounded-full bg-[#00d4ff] animate-pulse" />
          <span className="text-[#00d4ff] text-xs font-semibold tracking-widest uppercase">Bezpłatna konsultacja</span>
        </div>

        <h2 className="text-5xl md:text-7xl font-black text-white leading-tight mb-6">
          Gotowy na stronę,<br />
          <span
            className="text-transparent bg-clip-text"
            style={{ backgroundImage: 'linear-gradient(135deg, #00d4ff, #00ff88)' }}
          >
            która sprzedaje?
          </span>
        </h2>

        <p className="text-lg text-white/50 mb-12 max-w-xl mx-auto leading-relaxed">
          Umów bezpłatną konsultację i poznaj nasze podejście. Bez zobowiązań, konkretna rozmowa o Twoim projekcie.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
          <Link
            to="/kontakt"
            className="group relative overflow-hidden bg-[#00d4ff] text-black px-10 py-5 rounded-xl font-bold text-base hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer shadow-[0_0_50px_rgba(0,212,255,0.35)]"
          >
            <span className="relative z-10 flex items-center gap-2">
              Umów konsultację
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
            </span>
            <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity" />
          </Link>
          <Link
            to="/kontakt#wycena"
            className="group flex items-center gap-2 px-10 py-5 rounded-xl font-semibold text-base border border-white/20 text-white hover:border-[#00d4ff]/50 hover:bg-[#00d4ff]/5 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-file-list-3-line" />
            Poproś o wycenę
          </Link>
        </div>

        {/* Trust badges */}
        <div className="flex flex-wrap items-center justify-center gap-6 text-white/30 text-sm">
          <div className="flex items-center gap-2">
            <i className="ri-shield-check-line text-[#00d4ff]" />
            <span>Bez zobowiązań</span>
          </div>
          <div className="w-px h-4 bg-white/10" />
          <div className="flex items-center gap-2">
            <i className="ri-time-line text-[#00ff88]" />
            <span>Odpowiedź w 24h</span>
          </div>
          <div className="w-px h-4 bg-white/10" />
          <div className="flex items-center gap-2">
            <i className="ri-star-line text-yellow-400" />
            <span>5.0 / 5.0 opinie</span>
          </div>
          <div className="w-px h-4 bg-white/10" />
          <div className="flex items-center gap-2">
            <i className="ri-map-pin-line text-[#ff6b9d]" />
            <span>Warszawa & cała Polska</span>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pulse-slow {
          0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
          50% { transform: translate(-50%, -50%) scale(1.1); opacity: 0.7; }
        }
      `}</style>
    </section>
  );
}
